import { components as c } from "@/components";
import Spinner from "@/components/common/Spinner";
import { TrackerAdminToolsSection } from "@/features/templates/components/diary-summary/TrackerAdminToolsSection";
import { TrackersAdminTable } from "@/features/templates/components/TrackersAdminTable";
import { orderTrackersBySortOrder } from "@/features/templates/utils/trackerSortOrder";
import { resolveAllowExtraScores } from "@/features/templates/utils/allowExtraScoresDefault";
import { mergeTemplateAiDraftIntoSearch, templateWizardHasSupportedEvent } from "@/features/ai-builder/templateDraft";
import {
    TEMPLATE_AI_DRAFT_UNAVAILABLE,
    hasTemplateAiDraftMarker,
    searchForTemplateAiWizardNavigation,
    useTemplateAiDraftHydrated,
    useTemplateAiDraftStore,
} from "@/features/ai-builder/useTemplateAiDraftStore";
import { useTemplateDraftCatalogues } from "@/features/ai-builder/useTemplateDraftCatalogues";
import {
    BuilderFormSelectDialog,
    BuilderScheduleSelectDialog,
} from "@/features/builder/components/BuilderSelectDialogs";
import { filterBuilderPickerItems } from "@/features/builder/utils/builderAccess";
import { useSelectFormsLite } from "@/features/forms/api/selectForms";
import { toast } from "@patient-watch/ui/sonner";
import { useSchedulesQuery } from "@/features/schedules/api/selectSchedules";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { LockToggle, TemplateLockedRegion } from "@/features/templates/components/TemplateScopeLock";
import { FormFieldLabelRow } from "@patient-watch/ui/form";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useTemplateLocks } from "@/features/templates/hooks/useTemplateLocks";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useTemplateAdminView } from "@/features/templates/hooks/useTemplateAdminView";
import { useAuth } from "@/utils/auth";
import { templateSettingsSchema, type TemplateSettingsSchemaT } from "@patient-watch/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CopyIcon, TrashIcon, ChevronDown, ChevronUp } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useFieldArray, useForm, useWatch, type Control } from "react-hook-form";
import type { z } from "zod";

type TrackerFormValues = Pick<z.infer<typeof templateSettingsSchema>, "trackers" | "consents">;

const TrackerFormPage = () => {
    const { templateId } = Route.useParams();
    const locks = useTemplateLocks(templateId);
    const canConfigureLocks = locks.canConfigureLocks;
    const search = Route.useSearch();
    const auth = useAuth();
    const hydrated = useTemplateAiDraftHydrated();
    const storedDraft = useTemplateAiDraftStore((state) =>
        auth?.id && state.owners[templateId] === auth.id ? state.drafts[templateId] : undefined
    );
    const hasAiMarker = Boolean(auth?.id) && hasTemplateAiDraftMarker(templateId, auth.id);
    const isMissingAiDraft = hydrated && hasAiMarker && !storedDraft;
    const wizardSearch = mergeTemplateAiDraftIntoSearch(search, storedDraft);
    const catalogues = useTemplateDraftCatalogues();
    const { locale } = useAppLocale();
    const isAdmin = auth?.roles?.isAdmin || auth?.isSuperUser;
    const { isAdminView } = useTemplateAdminView();
    const { data: forms = [] } = useSelectFormsLite();
    const { data: schedules = [] } = useSchedulesQuery();
    const { markStepComplete, nextStep, setCurrentStep } = useTemplateNavigationStore();
    const navigate = useNavigate();
    const draftGeneration = useTemplateAiDraftStore((state) => state.generations[templateId] ?? 0);
    const appliedDraftKey = useRef<string | null>(null);
    const allowEmptyTrackers = Boolean(storedDraft) && templateWizardHasSupportedEvent(wizardSearch.required_events);

    useEffect(() => {
        setCurrentStep("trackers");
    }, [setCurrentStep]);

    const rawTrackers = wizardSearch.trackers || [];
    const trackers =
        rawTrackers.length > 0
            ? orderTrackersBySortOrder(
                  rawTrackers.map((t) => ({
                      form_id: t.form_id ?? "",
                      schedule_id: t.schedule_id ?? "",
                      back_up_schedule_id: t.back_up_schedule_id ?? undefined,
                      is_hidden: t.is_hidden ?? false,
                      // Leave unset so TrackersAdminTable can default from schedule score counts
                      allow_extra_scores: t.allow_extra_scores,
                      sort_order: t.sort_order ?? 0,
                      // null/empty = all roles; default new trackers to ["patient"]
                      roles: t.roles ?? ["patient"],
                      locked: t.locked,
                      row_id: t.row_id,
                      blocked_by_form_id: t.blocked_by_form_id,
                  }))
              )
            : allowEmptyTrackers
              ? []
              : [{ form_id: "", schedule_id: "", is_hidden: false, sort_order: 1, roles: ["patient"] }];

    const form = useForm<TrackerFormValues>({
        resolver: zodResolver(
            templateSettingsSchema.pick({
                trackers: true,
                consents: true,
            })
        ),
        defaultValues: {
            trackers,
            consents: (wizardSearch.consents ?? []).map((consent) => ({
                consent_body_id: consent.consent_body_id,
                roles: consent.roles ?? ["patient"],
                locked: consent.locked,
                row_id: consent.row_id,
            })),
        },
    });

    useLayoutEffect(() => {
        if (!hydrated || !storedDraft) return;
        const appliedKey = `${templateId}:${draftGeneration}`;
        if (appliedDraftKey.current === appliedKey) return;
        appliedDraftKey.current = appliedKey;
        form.reset({
            trackers,
            consents: (wizardSearch.consents ?? []).map((consent) => ({
                consent_body_id: consent.consent_body_id,
                roles: consent.roles ?? ["patient"],
                locked: consent.locked,
                row_id: consent.row_id,
            })),
        });
    }, [form, hydrated, storedDraft, templateId, draftGeneration, trackers, wizardSearch.consents]);

    useEffect(() => {
        if (!storedDraft || catalogues.isPending) return;
        const catalogueError = catalogues.validateWizardSettings(wizardSearch);
        if (catalogueError) toast.error(catalogueError);
        // Recheck once catalogues are ready for this draft, not on every keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedDraft, catalogues.isPending, templateId]);

    const { fields, append, remove, move } = useFieldArray({
        control: form.control,
        name: "trackers",
    });

    const renumberTrackerSortOrders = useCallback(() => {
        const current = form.getValues("trackers") ?? [];
        current.forEach((_, index) => {
            form.setValue(`trackers.${index}.sort_order`, index + 1, { shouldDirty: true });
        });
    }, [form]);

    const localText =
        locale === "fr-FR"
            ? {
                  questionnaires: "Questionnaires",
                  addExtraQuestionnaire: "Ajouter un questionnaire supplémentaire",
                  dataCollection: "Collecte de données",
                  selectQuestionnaire: "Sélectionner un questionnaire",
                  patientQuestions: "À quelles questions le patient doit-il répondre ?",
                  selectSchedule: "Sélectionner un calendrier",
                  schedulePrompt: "Quand le patient doit-il remplir le questionnaire ?",
                  moveFormUp: "Monter le formulaire",
                  moveFormDown: "Descendre le formulaire",
                  addConsent: "Ajouter un consentement",
                  trackerOrderHelp:
                      "Faites glisser les lignes pour définir l'ordre de déverrouillage. Les numéros d'ordre restent synchronisés avec la position de la ligne - modifiez un numéro pour déplacer la ligne.",
                  consents: "Consentements",
                  consentOrderHelp:
                      "Faites glisser les lignes pour définir l'ordre de signature. Les numéros d'ordre restent synchronisés avec la position de la ligne. Seuls les consentements publiés depuis le générateur de consentements apparaissent ici.",
              }
            : locale === "it-IT"
              ? {
                    questionnaires: "Questionari",
                    addExtraQuestionnaire: "Aggiungi un questionario aggiuntivo",
                    dataCollection: "Raccolta dati",
                    selectQuestionnaire: "Seleziona un questionario",
                    patientQuestions: "A quali domande deve rispondere il paziente?",
                    selectSchedule: "Seleziona un calendario",
                    schedulePrompt: "Quando deve compilare il questionario il paziente?",
                    moveFormUp: "Sposta il modulo in alto",
                    moveFormDown: "Sposta il modulo in basso",
                    addConsent: "Aggiungi un consenso",
                    trackerOrderHelp:
                        "Trascini le righe per definire l'ordine di sblocco. I numeri d'ordine restano allineati alla posizione della riga - modifichi un numero per spostare la riga.",
                    consents: "Consensi",
                    consentOrderHelp:
                        "Trascini le righe per definire l'ordine di firma. I numeri d'ordine restano allineati alla posizione della riga. Qui compaiono solo i consensi pubblicati dal generatore di consensi.",
                }
              : locale === "es-ES"
                ? {
                      questionnaires: "Cuestionarios",
                      addExtraQuestionnaire: "Añadir un cuestionario adicional",
                      dataCollection: "Recogida de datos",
                      selectQuestionnaire: "Seleccionar un cuestionario",
                      patientQuestions: "¿A qué preguntas debe responder el paciente?",
                      selectSchedule: "Seleccionar un calendario",
                      schedulePrompt: "¿Cuándo debe cumplimentar el cuestionario el paciente?",
                      moveFormUp: "Mover el formulario hacia arriba",
                      moveFormDown: "Mover el formulario hacia abajo",
                      addConsent: "Añadir un consentimiento",
                      trackerOrderHelp:
                          "Arrastre las filas para definir el orden de desbloqueo. Los números de orden siguen la posición de la fila - modifique un número para mover la fila.",
                      consents: "Consentimientos",
                      consentOrderHelp:
                          "Arrastre las filas para definir el orden de firma. Los números de orden siguen la posición de la fila. Aquí solo aparecen los consentimientos publicados desde el generador de consentimientos.",
                  }
                : locale === "de-DE"
                  ? {
                        questionnaires: "Fragebögen",
                        addExtraQuestionnaire: "Zusätzlichen Fragebogen hinzufügen",
                        dataCollection: "Datenerfassung",
                        selectQuestionnaire: "Fragebogen auswählen",
                        patientQuestions: "Welche Fragen soll der Patient beantworten?",
                        selectSchedule: "Zeitplan auswählen",
                        schedulePrompt: "Wann soll der Patient den Fragebogen ausfüllen?",
                        moveFormUp: "Formular nach oben verschieben",
                        moveFormDown: "Formular nach unten verschieben",
                        addConsent: "Einwilligung hinzufügen",
                        trackerOrderHelp:
                            "Ziehen Sie die Zeilen, um die Freigabereihenfolge festzulegen. Die Ordnungsnummern bleiben mit der Zeilenposition synchron – bearbeiten Sie eine Nummer, um die Zeile zu verschieben.",
                        consents: "Einwilligungen",
                        consentOrderHelp:
                            "Ziehen Sie die Zeilen, um die Unterschriftsreihenfolge festzulegen. Die Ordnungsnummern bleiben mit der Zeilenposition synchron. Hier erscheinen nur veröffentlichte Einwilligungen aus dem Einwilligungseditor.",
                    }
                  : locale === "da-DK"
                    ? {
                          questionnaires: "Spørgeskemaer",
                          addExtraQuestionnaire: "Tilføj ekstra spørgeskema",
                          dataCollection: "Dataindsamling",
                          selectQuestionnaire: "Vælg spørgeskema",
                          patientQuestions: "Hvilke spørgsmål skal patienten besvare?",
                          selectSchedule: "Vælg tidsplan",
                          schedulePrompt: "Hvornår skal patienten udfylde spørgeskemaet?",
                          moveFormUp: "Flyt formular op",
                          moveFormDown: "Flyt formular ned",
                          addConsent: "Tilføj samtykke",
                          trackerOrderHelp:
                              "Træk rækkerne for at angive oplåsningsrækkefølgen. Numrene følger rækkernes placering – rediger et nummer for at flytte rækken.",
                          consents: "Samtykker",
                          consentOrderHelp:
                              "Træk rækkerne for at angive underskriftsrækkefølgen. Numrene følger rækkernes placering. Kun offentliggjorte samtykker fra samtykkebyggeren vises her.",
                      }
                    : {
                          questionnaires: "Questionnaires",
                          addExtraQuestionnaire: "Add extra questionnaire",
                          dataCollection: "Data Collection",
                          selectQuestionnaire: "Select Questionnaire",
                          patientQuestions: "What questions should the patient answer?",
                          selectSchedule: "Select Schedule",
                          schedulePrompt: "When should the patient complete the questionnaire?",
                          moveFormUp: "Move form up",
                          moveFormDown: "Move form down",
                          addConsent: "Add Consent",
                          trackerOrderHelp:
                              "Drag rows to set unlock order. Order numbers stay in sync with row position - edit a number to move that row.",
                          consents: "Consents",
                          consentOrderHelp:
                              "Drag rows to set signing order. Order numbers stay in sync with row position. Only published consents from the Consent builder appear here.",
                      };

    // Watch trackers to enable/disable button
    const watchedTrackers = useWatch({
        control: form.control,
        name: "trackers",
    });

    const handleAddTracker = () => {
        append({
            form_id: "",
            schedule_id: "",
            is_hidden: false,
            sort_order: fields.length + 1,
            roles: ["patient"],
            // allow_extra_scores left unset until schedules are chosen
        });
    };

    const handleDuplicateTracker = (index: number) => {
        const formTrackers = form.getValues("trackers");
        const trackerToDuplicate = formTrackers[index];
        if (trackerToDuplicate) {
            append({
                ...trackerToDuplicate,
                locked: false,
                row_id: undefined,
                sort_order: formTrackers.length + 1,
            });
            queueMicrotask(() => renumberTrackerSortOrders());
        }
    };

    const handleNext = async () => {
        const isValid = await form.trigger("trackers");
        console.log("Form validation result:", isValid);

        if (isValid) {
            const scheduleById = new Map(
                (schedules ?? [])
                    .filter((schedule) => schedule.schedule_id)
                    .map((schedule) => [schedule.schedule_id as string, schedule])
            );
            const formTrackers = form.getValues("trackers").map((tracker, index) => ({
                ...tracker,
                schedule_id: tracker.schedule_id || undefined,
                back_up_schedule_id: tracker.back_up_schedule_id || undefined,
                sort_order: Number(tracker.sort_order ?? index + 1),
                allow_extra_scores: resolveAllowExtraScores(tracker.allow_extra_scores, {
                    schedule: tracker.schedule_id ? scheduleById.get(tracker.schedule_id) : null,
                    backUpSchedule: tracker.back_up_schedule_id ? scheduleById.get(tracker.back_up_schedule_id) : null,
                }),
            }));
            const searchWithoutSequentialForms = { ...wizardSearch };
            delete (searchWithoutSequentialForms as { sequential_forms?: boolean }).sequential_forms;
            const nextSettings = {
                ...searchWithoutSequentialForms,
                trackers: formTrackers,
            };
            if (storedDraft) {
                const catalogueError = catalogues.validateWizardSettings(nextSettings);
                if (catalogueError) {
                    toast.error(catalogueError);
                    return;
                }
            }
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            console.log("handleNext called with trackers:", formTrackers);
            console.log(
                "All trackers valid?",
                formTrackers.every((t) => Boolean(t.form_id))
            );

            markStepComplete("trackers");
            nextStep();
            console.log("Navigating to events page");
            void navigate({
                to: "/templates/$templateId/events",
                params: { templateId },
                search: wizardSearchPayload,
            });
        } else {
            console.log("Trackers not valid, not navigating");
        }
    };

    if (!auth?.id || (hasAiMarker && !hydrated)) {
        return (
            <div className="flex justify-center py-16">
                <Spinner className="w-10 h-10" />
            </div>
        );
    }

    if (isMissingAiDraft) {
        return (
            <c.ui.Card>
                <c.ui.CardHeader>
                    <c.ui.CardTitle>Your AI draft is no longer available</c.ui.CardTitle>
                    <c.ui.CardDescription>{TEMPLATE_AI_DRAFT_UNAVAILABLE}</c.ui.CardDescription>
                </c.ui.CardHeader>
                <c.ui.CardFooter>
                    <c.ui.Button
                        onClick={() => {
                            useTemplateAiDraftStore.getState().removeDraft(templateId);
                            void navigate({ to: "/templates" });
                        }}
                    >
                        Regenerate with AI
                    </c.ui.Button>
                </c.ui.CardFooter>
            </c.ui.Card>
        );
    }

    return (
        <div className="space-y-8">
            <c.ui.Form {...form}>
                {isAdminView ? (
                    <div className="space-y-3">
                        <div className="flex justify-between items-start gap-4">
                            <div className="space-y-1 min-w-0">
                                <h2 className="font-medium text-base">{localText.questionnaires}</h2>
                                <p className="text-muted-foreground text-sm">{localText.trackerOrderHelp}</p>
                            </div>
                            <c.fc.AddButton
                                buttonType="button"
                                variant="outline"
                                size="sm"
                                className="w-fit shrink-0"
                                label={localText.addExtraQuestionnaire}
                                onClick={handleAddTracker}
                            />
                        </div>
                        <TrackersAdminTable
                            form={form}
                            fields={fields}
                            control={form.control}
                            forms={forms}
                            schedules={schedules ?? []}
                            onDuplicate={handleDuplicateTracker}
                            onRemove={remove}
                            onMove={move}
                            onRenumber={renumberTrackerSortOrders}
                            canConfigureLocks={canConfigureLocks}
                        />
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <h2 className="font-medium text-base">{localText.questionnaires}</h2>
                            <c.fc.AddButton
                                buttonType="button"
                                variant="outline"
                                size="sm"
                                className="w-fit"
                                label={localText.addExtraQuestionnaire}
                                onClick={handleAddTracker}
                            />
                        </div>
                        {fields.map((field, index) => {
                            const rowLocked = watchedTrackers?.[index]?.locked === true;
                            return (
                                <c.ui.Card key={field.id}>
                                    <c.ui.CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
                                        <FormFieldLabelRow
                                            label={
                                                <c.ui.CardTitle className="text-base">
                                                    {localText.dataCollection} {index + 1}
                                                </c.ui.CardTitle>
                                            }
                                            trailing={
                                                <LockToggle
                                                    locked={rowLocked}
                                                    canConfigure={canConfigureLocks}
                                                    disabled={!locks.canConfigure}
                                                    onChange={(value) =>
                                                        form.setValue(`trackers.${index}.locked`, value, {
                                                            shouldDirty: true,
                                                        })
                                                    }
                                                />
                                            }
                                        />
                                        <div className="flex items-center gap-2">
                                            <c.ui.Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    move(index, index - 1);
                                                    queueMicrotask(() => renumberTrackerSortOrders());
                                                }}
                                                disabled={rowLocked || index === 0}
                                                aria-label={localText.moveFormUp}
                                            >
                                                <ChevronUp size={20} />
                                            </c.ui.Button>
                                            <c.ui.Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    move(index, index + 1);
                                                    queueMicrotask(() => renumberTrackerSortOrders());
                                                }}
                                                disabled={rowLocked || index === fields.length - 1}
                                                aria-label={localText.moveFormDown}
                                            >
                                                <ChevronDown size={20} />
                                            </c.ui.Button>
                                            <c.ui.Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                disabled={rowLocked}
                                                onClick={() => handleDuplicateTracker(index)}
                                            >
                                                <CopyIcon size={20} />
                                            </c.ui.Button>
                                            <c.ui.Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    remove(index);
                                                    queueMicrotask(() => renumberTrackerSortOrders());
                                                }}
                                                disabled={rowLocked || (index === 0 && fields.length === 1)}
                                            >
                                                <TrashIcon size={20} />
                                            </c.ui.Button>
                                        </div>
                                    </c.ui.CardHeader>
                                    <c.ui.CardContent className="flex flex-col gap-4">
                                        <div className="flex md:flex-row flex-col items-end gap-4">
                                            <TemplateLockedRegion
                                                locked={rowLocked}
                                                className="flex-grow w-full md:max-w-[50%] md:basis-1/2"
                                            >
                                                <BuilderFormSelectDialog
                                                    name={`trackers.${index}.form_id`}
                                                    control={form.control}
                                                    options={
                                                        filterBuilderPickerItems(
                                                            forms?.filter((f) => !!f.form_id) || [],
                                                            {
                                                                isAdmin,
                                                                userId: auth.id,
                                                            }
                                                        ) as any
                                                    }
                                                    label={index === 0 ? localText.selectQuestionnaire : undefined}
                                                    description={index === 0 ? localText.patientQuestions : undefined}
                                                    locale={locale}
                                                />
                                            </TemplateLockedRegion>

                                            <TemplateLockedRegion
                                                locked={rowLocked}
                                                className="flex-grow w-full md:max-w-[50%] md:basis-1/2"
                                            >
                                                <BuilderScheduleSelectDialog
                                                    name={`trackers.${index}.schedule_id`}
                                                    control={form.control}
                                                    options={filterBuilderPickerItems(schedules ?? [], {
                                                        isAdmin,
                                                        userId: auth.id,
                                                    })}
                                                    label={index === 0 ? localText.selectSchedule : undefined}
                                                    description={
                                                        index === 0 ? <>{localText.schedulePrompt} </> : undefined
                                                    }
                                                    locale={locale}
                                                />
                                            </TemplateLockedRegion>
                                        </div>

                                        <TrackerAdminToolsSection
                                            index={index}
                                            control={form.control as unknown as Control<TemplateSettingsSchemaT>}
                                            schedules={schedules ?? []}
                                            isAdmin={false}
                                        />
                                    </c.ui.CardContent>
                                </c.ui.Card>
                            );
                        })}
                    </div>
                )}
            </c.ui.Form>

            <TemplateStepFooter
                onPrimaryClick={handleNext}
                disabledPrimary={
                    !watchedTrackers ||
                    (watchedTrackers.length === 0
                        ? !allowEmptyTrackers
                        : !watchedTrackers.every((t) => Boolean(t.form_id)))
                }
            />
        </div>
    );
};

// Use partial schema to allow any template settings fields
const trackersSearchSchema = templateSettingsSchema.partial();

// Transform null strings to actual null values or undefined for optional fields
const transformSearch = (search: any) => {
    const transformed = { ...search };
    const isNilString = (value: unknown) => value === "null" || value === "";
    const normalizeOptionalUuid = (value: unknown) => (isNilString(value) || value == null ? undefined : value);

    // Fields that should be null when empty
    const nullableFields = [
        "contact_id",
        "organisation_id",
        "organisation_name",
        "hospital_number",
        "referring_clinician_id",
        "injecting_clinician_id",
        "shockwaving_clinician_id",
        "operating_clinician_id",
        "educated_by",
        "injection_method_id",
        "injection_id",
        "surgery_id",
        "education_id",
        "shockwave_id",
        "side",
        "orthopaedic_area",
        "surgery_option_id",
        "education_name",
        "education_description",
    ];
    // Fields that should be undefined when empty (optional but not nullable)
    const optionalFields = [
        "description",
        "invited_by",
        "invited_by_formal_name",
        // Arrays in `templateSettingsSchema` are optional (not nullable).
        "injection_details",
        "shockwave_details",
    ];

    // Nested: normalise optional UUIDs inside detail arrays so routing doesn't hard-fail.
    if (Array.isArray(transformed.injection_details)) {
        transformed.injection_details = transformed.injection_details.map((d: any) => ({
            ...d,
            injection_detail_id: normalizeOptionalUuid(d?.injection_detail_id),
            injection_id: normalizeOptionalUuid(d?.injection_id),
        }));
    }
    if (Array.isArray(transformed.shockwave_details)) {
        transformed.shockwave_details = transformed.shockwave_details.map((d: any) => ({
            ...d,
            shockwave_detail_id: normalizeOptionalUuid(d?.shockwave_detail_id),
            shockwave_id: normalizeOptionalUuid(d?.shockwave_id),
        }));
    }
    if (Array.isArray(transformed.trackers)) {
        transformed.trackers = transformed.trackers.map((t: any) => ({
            ...t,
            back_up_schedule_id: normalizeOptionalUuid(t?.back_up_schedule_id),
        }));
    }

    for (const key in transformed) {
        if (isNilString(transformed[key])) {
            if (nullableFields.includes(key)) {
                transformed[key] = null;
            } else if (optionalFields.includes(key)) {
                delete transformed[key]; // Remove to make it undefined
            }
        }
    }
    return transformed;
};

export const Route = createFileRoute("/_app/templates/$templateId/trackers")({
    component: TrackerFormPage,
    validateSearch: (search) => trackersSearchSchema.parse(transformSearch(search)),
});
